import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Language options
const LANGUAGES = [
  { code: 'hr', name: 'Hrvatski', flag: '🇭🇷' },
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'it', name: 'Italiano', flag: '🇮🇹' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' }
];

// Border radius options
const BORDER_RADIUS_OPTIONS = [
  { value: 'none', label: 'Bez zaobljenja', labelEn: 'No rounding', preview: '0px' },
  { value: 'small', label: 'Malo zaobljenje', labelEn: 'Small rounding', preview: '6px' },
  { value: 'medium', label: 'Srednje zaobljenje', labelEn: 'Medium rounding', preview: '12px' },
  { value: 'large', label: 'Veliko zaobljenje', labelEn: 'Large rounding', preview: '20px' }
];

// Popular Google Fonts - Serif for headings
const HEADING_FONT_OPTIONS = [
  // Elegantni serif fontovi
  "Playfair Display",
  "Cormorant Garamond",
  "Merriweather",
  "Lora",
  "Crimson Text",
  "Libre Baskerville",
  "EB Garamond",
  "Source Serif Pro",
  "PT Serif",
  "Bitter",
  // Display fontovi - upečatljivi
  "Abril Fatface",
  "Bodoni Moda",
  "Cinzel",
  "Marcellus",
  "Yeseva One",
  // Moderni fontovi
  "Bebas Neue",
  "Oswald",
  "Archivo Black",
  "Anton",
  "Righteous",
  // Script i rukopisni fontovi
  "Dancing Script",
  "Pacifico",
  "Great Vibes",
  "Satisfy",
  "Sacramento",
  // Sans-serif za moderne dizajne
  "Poppins",
  "Montserrat",
  "Raleway",
  "Quicksand",
  "Comfortaa"
];

// Popular Google Fonts - Sans-serif for body
const BODY_FONT_OPTIONS = [
  "Inter",
  "Roboto",
  "Open Sans",
  "Lato",
  "Montserrat",
  "Poppins",
  "Raleway",
  "Nunito",
  "Source Sans Pro",
  "PT Sans",
  "Ubuntu",
  "Work Sans",
  "DM Sans",
  "Outfit"
];

const ColorInput = ({ label, description, value, onChange, name }) => (
  <div className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
    <div className="flex-shrink-0">
      <input
        type="color"
        value={value || '#000000'}
        onChange={(e) => onChange(name, e.target.value)}
        className="w-12 h-12 rounded cursor-pointer border-2 border-gray-300"
      />
    </div>
    <div className="flex-1">
      <Label className="font-semibold text-sm">{label}</Label>
      <p className="text-xs text-gray-500 mt-0.5">{description}</p>
    </div>
    <div className="flex-shrink-0">
      <Input
        type="text"
        value={value || ''}
        onChange={(e) => onChange(name, e.target.value)}
        placeholder="#000000"
        className="w-28 text-sm font-mono"
      />
    </div>
  </div>
);

const ThemeSettings = () => {
  const [theme, setTheme] = useState({
    property_type: "camp",
    brand_name: "RijekaBooking",
    logo_url: "",
    favicon_url: "",
    hero_background_url: "",
    // Hero title per language
    hero_title_hr: "Luksuzni smještaji u Rijeci",
    hero_title_en: "Luxury Properties in Rijeka",
    hero_title_it: "Alloggi di Lusso a Fiume",
    hero_title_de: "Luxusunterkünfte in Rijeka",
    // Hero subtitle per language
    hero_subtitle_hr: "Doživite vrhunski smještaj s dostupnošću u stvarnom vremenu, profesionalnom uslugom i trenutnom potvrdom rezervacije.",
    hero_subtitle_en: "Experience premium accommodations with real-time availability, professional service, and instant booking confirmation.",
    hero_subtitle_it: "Vivi alloggi premium con disponibilità in tempo reale, servizio professionale e conferma immediata della prenotazione.",
    hero_subtitle_de: "Erleben Sie erstklassige Unterkünfte mit Echtzeit-Verfügbarkeit, professionellem Service und sofortiger Buchungsbestätigung.",
    // Section title per language (Why Book With Us)
    section_title_hr: "Zašto rezervirati kod nas",
    section_title_en: "Why Book With Us",
    section_title_it: "Perché prenotare con noi",
    section_title_de: "Warum bei uns buchen",
    // Section subtitle per language
    section_subtitle_hr: "Optimizirali smo svaki aspekt vašeg iskustva rezervacije",
    section_subtitle_en: "We've optimized every aspect of your booking experience",
    section_subtitle_it: "Abbiamo ottimizzato ogni aspetto della tua esperienza di prenotazione",
    section_subtitle_de: "Wir haben jeden Aspekt Ihrer Buchungserfahrung optimiert",
    font_heading: "Playfair Display",
    font_heading_weight: "600",
    font_body: "Inter",
    border_radius: "medium",
    color_calendar_selection: "#C9A961",
    color_primary: "#C9A961",
    color_primary_hover: "#B8984F",
    color_primary_dark: "#AD864C",
    color_primary_light: "#E7CA93",
    color_dark: "#35393C",
    color_dark_hover: "#2A2D2F",
    color_background: "#EBE9E4",
    color_background_light: "#F5F3EE",
    color_background_white: "#FFFFFF",
    color_border: "#D3D0C7",
    color_border_light: "#E5E5E5",
    color_text_primary: "#35393C",
    color_text_secondary: "#57534e",
    color_text_muted: "#78716c",
    color_text_white: "#FFFFFF"
  });
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedHeroLang, setSelectedHeroLang] = useState('hr');

  useEffect(() => {
    fetchTheme();
  }, []);

  const fetchTheme = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/admin/theme`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTheme(prev => ({ ...prev, ...response.data }));
    } catch (error) {
      console.error('Error fetching theme:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (name, value) => {
    setTheme(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('admin_token');
      await axios.post(`${API}/admin/theme`, theme, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Theme settings saved successfully! Refresh the page to see changes.');
    } catch (error) {
      console.error('Error saving theme:', error);
      toast.error('Failed to save theme settings');
    } finally {
      setSaving(false);
    }
  };

  const resetToDefaults = () => {
    setTheme({
      brand_name: "RijekaBooking",
      logo_url: "",
      favicon_url: "",
      hero_background_url: "",
      hero_title_hr: "Luksuzni smještaji u Rijeci",
      hero_title_en: "Luxury Properties in Rijeka",
      hero_title_it: "Alloggi di Lusso a Fiume",
      hero_title_de: "Luxusunterkünfte in Rijeka",
      hero_subtitle_hr: "Doživite vrhunski smještaj s dostupnošću u stvarnom vremenu, profesionalnom uslugom i trenutnom potvrdom rezervacije.",
      hero_subtitle_en: "Experience premium accommodations with real-time availability, professional service, and instant booking confirmation.",
      hero_subtitle_it: "Vivi alloggi premium con disponibilità in tempo reale, servizio professionale e conferma immediata della prenotazione.",
      hero_subtitle_de: "Erleben Sie erstklassige Unterkünfte mit Echtzeit-Verfügbarkeit, professionellem Service und sofortiger Buchungsbestätigung.",
      section_title_hr: "Zašto rezervirati kod nas",
      section_title_en: "Why Book With Us",
      section_title_it: "Perché prenotare con noi",
      section_title_de: "Warum bei uns buchen",
      section_subtitle_hr: "Optimizirali smo svaki aspekt vašeg iskustva rezervacije",
      section_subtitle_en: "We've optimized every aspect of your booking experience",
      section_subtitle_it: "Abbiamo ottimizzato ogni aspetto della tua esperienza di prenotazione",
      section_subtitle_de: "Wir haben jeden Aspekt Ihrer Buchungserfahrung optimiert",
      font_heading: "Playfair Display",
      font_body: "Inter",
      color_calendar_selection: "#C9A961",
      color_primary: "#C9A961",
      color_primary_hover: "#B8984F",
      color_primary_dark: "#AD864C",
      color_primary_light: "#E7CA93",
      color_dark: "#35393C",
      color_dark_hover: "#2A2D2F",
      color_background: "#EBE9E4",
      color_background_light: "#F5F3EE",
      color_background_white: "#FFFFFF",
      color_border: "#D3D0C7",
      color_border_light: "#E5E5E5",
      color_text_primary: "#35393C",
      color_text_secondary: "#57534e",
      color_text_muted: "#78716c",
      color_text_white: "#FFFFFF"
    });
    toast.info('Reset to default values. Click Save to apply.');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin h-8 w-8 border-4 border-[var(--color-primary)] border-r-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Preview Bar */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Live Preview</CardTitle>
        </CardHeader>
        <CardContent>
          <div 
            className="rounded-lg overflow-hidden shadow-lg"
            style={{ backgroundColor: theme.color_background }}
          >
            {/* Preview Hero with Background Image */}
            <div 
              className="relative px-6 py-12 flex items-center justify-center"
              style={{ 
                backgroundColor: theme.color_dark,
                backgroundImage: theme.hero_background_url ? `url(${theme.hero_background_url})` : 'none',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                minHeight: '200px'
              }}
            >
              {theme.hero_background_url && (
                <div className="absolute inset-0 bg-black/50"></div>
              )}
              <div className="relative z-10 text-center">
                {theme.logo_url && (
                  <img src={theme.logo_url} alt="Logo" className="h-12 w-auto mx-auto mb-4" />
                )}
                <h2 
                  className="text-2xl font-bold mb-2"
                  style={{ 
                    color: theme.color_text_white, 
                    fontFamily: `"${theme.font_heading}", serif` 
                  }}
                >
                  {theme[`hero_title_${selectedHeroLang}`] || 'Hero Title'}
                </h2>
                <p 
                  className="text-sm opacity-90"
                  style={{ 
                    color: theme.color_text_white,
                    fontFamily: `"${theme.font_body}", sans-serif`
                  }}
                >
                  {(theme[`hero_subtitle_${selectedHeroLang}`] || '').substring(0, 80)}...
                </p>
              </div>
            </div>
            
            {/* Preview Content */}
            <div className="p-6">
              <h2 
                className="text-xl font-bold mb-4"
                style={{ color: theme.color_text_primary, fontFamily: `"${theme.font_heading}", serif` }}
              >
                Sample Heading (Serif)
              </h2>
              <p 
                className="mb-4"
                style={{ color: theme.color_text_secondary, fontFamily: `"${theme.font_body}", sans-serif` }}
              >
                This is sample body text (Sans-serif) showing how your content will look.
              </p>
              <div className="flex gap-3 items-center">
                <button 
                  className="px-4 py-2 rounded font-semibold transition-colors"
                  style={{ 
                    backgroundColor: theme.color_primary, 
                    color: theme.color_text_white,
                    fontFamily: `"${theme.font_body}", sans-serif`
                  }}
                >
                  Primary Button
                </button>
                <div 
                  className="w-8 h-8 rounded"
                  style={{ backgroundColor: theme.color_calendar_selection }}
                  title="Calendar Selection Color"
                />
                <span className="text-xs text-gray-500">← Calendar color</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Branding Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
            </svg>
            Branding
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="brand_name">Brand / Site Name</Label>
            <Input
              id="brand_name"
              value={theme.brand_name}
              onChange={(e) => handleChange('brand_name', e.target.value)}
              placeholder="Your Brand Name"
            />
            <p className="text-xs text-gray-500 mt-1">Used in page title (browser tab) and footer</p>
          </div>
          <div>
            <Label htmlFor="logo_url">Logo URL</Label>
            <Input
              id="logo_url"
              value={theme.logo_url || ''}
              onChange={(e) => handleChange('logo_url', e.target.value)}
              placeholder="https://example.com/logo.png"
            />
            <p className="text-xs text-gray-500 mt-1">Displayed in navigation bar and login page. Recommended: PNG or SVG, max height 50px</p>
          </div>
          <div>
            <Label htmlFor="favicon_url">Favicon URL</Label>
            <Input
              id="favicon_url"
              value={theme.favicon_url || ''}
              onChange={(e) => handleChange('favicon_url', e.target.value)}
              placeholder="https://example.com/favicon.ico"
            />
            <p className="text-xs text-gray-500 mt-1">Recommended: ICO or PNG, 32x32px</p>
          </div>
        </CardContent>
      </Card>

      {/* Hero Section with Language Tabs */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            Hero Section
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="hero_background_url">Background Image URL</Label>
            <Input
              id="hero_background_url"
              value={theme.hero_background_url || ''}
              onChange={(e) => handleChange('hero_background_url', e.target.value)}
              placeholder="https://example.com/hero-image.jpg"
            />
            <p className="text-xs text-gray-500 mt-1">High-quality image for homepage hero. Recommended: 1920x1080px or larger</p>
          </div>
          
          {/* Language Tabs */}
          <div className="border-t pt-4 mt-4">
            <Label className="mb-3 block">Hero Title & Subtitle (per language)</Label>
            <div className="flex gap-1 mb-4 bg-gray-100 p-1 rounded-lg">
              {LANGUAGES.map(lang => (
                <button
                  key={lang.code}
                  onClick={() => setSelectedHeroLang(lang.code)}
                  className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-all ${
                    selectedHeroLang === lang.code 
                      ? 'bg-white shadow text-[var(--color-text-primary)]' 
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <span className="mr-1">{lang.flag}</span>
                  <span className="hidden sm:inline">{lang.name}</span>
                  <span className="sm:hidden">{lang.code.toUpperCase()}</span>
                </button>
              ))}
            </div>
            
            {/* Title Input for Selected Language */}
            <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
              <div>
                <Label htmlFor={`hero_title_${selectedHeroLang}`}>
                  Hero Title ({LANGUAGES.find(l => l.code === selectedHeroLang)?.name})
                </Label>
                <Input
                  id={`hero_title_${selectedHeroLang}`}
                  value={theme[`hero_title_${selectedHeroLang}`] || ''}
                  onChange={(e) => handleChange(`hero_title_${selectedHeroLang}`, e.target.value)}
                  placeholder="Enter hero title..."
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label htmlFor={`hero_subtitle_${selectedHeroLang}`}>
                  Hero Subtitle ({LANGUAGES.find(l => l.code === selectedHeroLang)?.name})
                </Label>
                <textarea
                  id={`hero_subtitle_${selectedHeroLang}`}
                  value={theme[`hero_subtitle_${selectedHeroLang}`] || ''}
                  onChange={(e) => handleChange(`hero_subtitle_${selectedHeroLang}`, e.target.value)}
                  placeholder="Enter hero subtitle..."
                  className="w-full mt-1 p-2 border border-gray-300 rounded-md min-h-[80px]"
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Section Title (Why Book With Us) */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Section Titles (Above Info Cards)
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-gray-600 mb-4">
            Configure the "Why Book With Us" section title and subtitle that appear above the info cards on the homepage.
          </p>
          
          {/* Language Tabs for Section Title */}
          <div className="flex gap-1 mb-4 bg-gray-100 p-1 rounded-lg">
            {LANGUAGES.map(lang => (
              <button
                key={lang.code}
                onClick={() => setSelectedHeroLang(lang.code)}
                className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-all ${
                  selectedHeroLang === lang.code 
                    ? 'bg-white shadow text-[var(--color-text-primary)]' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <span className="mr-1">{lang.flag}</span>
                <span className="hidden sm:inline">{lang.name}</span>
                <span className="sm:hidden">{lang.code.toUpperCase()}</span>
              </button>
            ))}
          </div>
          
          {/* Title Input for Selected Language */}
          <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
            <div>
              <Label htmlFor={`section_title_${selectedHeroLang}`}>
                Section Title ({LANGUAGES.find(l => l.code === selectedHeroLang)?.name})
              </Label>
              <Input
                id={`section_title_${selectedHeroLang}`}
                value={theme[`section_title_${selectedHeroLang}`] || ''}
                onChange={(e) => handleChange(`section_title_${selectedHeroLang}`, e.target.value)}
                placeholder="e.g., Zašto rezervirati kod nas"
                className="mt-1"
              />
            </div>
            
            <div>
              <Label htmlFor={`section_subtitle_${selectedHeroLang}`}>
                Section Subtitle ({LANGUAGES.find(l => l.code === selectedHeroLang)?.name})
              </Label>
              <Input
                id={`section_subtitle_${selectedHeroLang}`}
                value={theme[`section_subtitle_${selectedHeroLang}`] || ''}
                onChange={(e) => handleChange(`section_subtitle_${selectedHeroLang}`, e.target.value)}
                placeholder="e.g., Optimizirali smo svaki aspekt vašeg iskustva rezervacije"
                className="mt-1"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Typography Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h8m-8 6h16" />
            </svg>
            Typography
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="font_heading">Heading Font (Serif)</Label>
              <select
                id="font_heading"
                value={theme.font_heading}
                onChange={(e) => handleChange('font_heading', e.target.value)}
                className="w-full mt-1 p-2 border border-gray-300 rounded-md"
              >
                {HEADING_FONT_OPTIONS.map(font => (
                  <option key={font} value={font}>{font}</option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">Used for titles and headings (H1, H2, etc.)</p>
            </div>
            <div>
              <Label htmlFor="font_heading_weight">Heading Font Weight</Label>
              <select
                id="font_heading_weight"
                value={theme.font_heading_weight || '600'}
                onChange={(e) => handleChange('font_heading_weight', e.target.value)}
                className="w-full mt-1 p-2 border border-gray-300 rounded-md"
              >
                <option value="300">300 - Light</option>
                <option value="400">400 - Normal</option>
                <option value="500">500 - Medium</option>
                <option value="600">600 - Semi Bold</option>
                <option value="700">700 - Bold</option>
                <option value="800">800 - Extra Bold</option>
                <option value="900">900 - Black</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">Font thickness for headings</p>
            </div>
          </div>
          <div>
            <Label htmlFor="font_body">Body Font (Sans-serif)</Label>
            <select
              id="font_body"
              value={theme.font_body}
              onChange={(e) => handleChange('font_body', e.target.value)}
              className="w-full mt-1 p-2 border border-gray-300 rounded-md"
            >
              {BODY_FONT_OPTIONS.map(font => (
                <option key={font} value={font}>{font}</option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">Used for paragraphs, buttons, and labels</p>
          </div>
        </CardContent>
      </Card>

      {/* Border Radius / Corner Rounding */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM14 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1v-4zM14 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z" />
            </svg>
            Corner Rounding
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Label className="mb-3 block">Zaobljenje kutova elemenata</Label>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {BORDER_RADIUS_OPTIONS.map(option => (
              <button
                key={option.value}
                type="button"
                onClick={() => handleChange('border_radius', option.value)}
                className={`p-4 border-2 transition-all ${
                  theme.border_radius === option.value 
                    ? 'border-[var(--color-primary)] bg-[var(--color-primary)]/10' 
                    : 'border-gray-200 hover:border-gray-300'
                }`}
                style={{ borderRadius: option.preview }}
              >
                <div 
                  className="w-full h-12 bg-gray-300 mb-2"
                  style={{ borderRadius: option.preview }}
                ></div>
                <p className="text-sm font-medium text-center">{option.label}</p>
                <p className="text-xs text-gray-500 text-center">{option.preview}</p>
              </button>
            ))}
          </div>
          <p className="text-xs text-gray-500 mt-3">Primjenjuje se na gumbe, kartice, input polja i sve ostale elemente</p>
        </CardContent>
      </Card>

      {/* Calendar Colors */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            Calendar
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ColorInput
            label="Date Selection Color"
            description="Color for selected date range in calendar picker"
            value={theme.color_calendar_selection}
            onChange={handleChange}
            name="color_calendar_selection"
          />
        </CardContent>
      </Card>

      {/* Primary Colors */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <div className="w-5 h-5 rounded" style={{ backgroundColor: theme.color_primary }}></div>
            Primary Colors (Accent)
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <ColorInput label="Primary Color" description="Main accent color - buttons, links, highlights" value={theme.color_primary} onChange={handleChange} name="color_primary" />
          <ColorInput label="Primary Hover" description="Hover state for primary buttons" value={theme.color_primary_hover} onChange={handleChange} name="color_primary_hover" />
          <ColorInput label="Primary Dark" description="Darker variant for emphasis" value={theme.color_primary_dark} onChange={handleChange} name="color_primary_dark" />
          <ColorInput label="Primary Light" description="Lighter variant for subtle accents" value={theme.color_primary_light} onChange={handleChange} name="color_primary_light" />
        </CardContent>
      </Card>

      {/* Dark/Neutral Colors */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <div className="w-5 h-5 rounded" style={{ backgroundColor: theme.color_dark }}></div>
            Dark / Neutral Colors
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <ColorInput label="Dark Color" description="Navbar, headers, dark backgrounds" value={theme.color_dark} onChange={handleChange} name="color_dark" />
          <ColorInput label="Dark Hover" description="Hover state for dark elements" value={theme.color_dark_hover} onChange={handleChange} name="color_dark_hover" />
        </CardContent>
      </Card>

      {/* Background Colors */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <div className="w-5 h-5 rounded border" style={{ backgroundColor: theme.color_background }}></div>
            Background Colors
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <ColorInput label="Page Background" description="Main page background color" value={theme.color_background} onChange={handleChange} name="color_background" />
          <ColorInput label="Light Background" description="Cards and sections background" value={theme.color_background_light} onChange={handleChange} name="color_background_light" />
          <ColorInput label="White Background" description="Pure white for forms and inputs" value={theme.color_background_white} onChange={handleChange} name="color_background_white" />
        </CardContent>
      </Card>

      {/* Border Colors */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <div className="w-5 h-5 rounded border-2" style={{ borderColor: theme.color_border }}></div>
            Border Colors
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <ColorInput label="Border Color" description="Input borders, card borders" value={theme.color_border} onChange={handleChange} name="color_border" />
          <ColorInput label="Light Border" description="Subtle dividers and separators" value={theme.color_border_light} onChange={handleChange} name="color_border_light" />
        </CardContent>
      </Card>

      {/* Text Colors */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Text Colors
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <ColorInput label="Primary Text" description="Main body text, headings" value={theme.color_text_primary} onChange={handleChange} name="color_text_primary" />
          <ColorInput label="Secondary Text" description="Subtitles, descriptions" value={theme.color_text_secondary} onChange={handleChange} name="color_text_secondary" />
          <ColorInput label="Muted Text" description="Hints, placeholders, labels" value={theme.color_text_muted} onChange={handleChange} name="color_text_muted" />
          <ColorInput label="White Text" description="Text on dark backgrounds" value={theme.color_text_white} onChange={handleChange} name="color_text_white" />
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <div className="flex gap-4 justify-end sticky bottom-4 bg-white p-4 rounded-lg shadow-lg border">
        <Button variant="outline" onClick={resetToDefaults}>
          Reset to Defaults
        </Button>
        <Button 
          onClick={handleSave} 
          disabled={saving}
          className="bg-[var(--color-primary)] hover:bg-[var(--color-primary-hover)] text-white px-8"
        >
          {saving ? 'Saving...' : 'Save Theme Settings'}
        </Button>
      </div>
    </div>
  );
};

export default ThemeSettings;
